import { displayDateTimeToMinute, Entity, FieldType, makeClone, Option, PersistenceType } from 'gossamer-unified';
import { Event } from './Event';
import { EventFeedback } from './EventFeedback';
import { Ticket } from './Ticket';
import { TicketBooking } from './TicketBooking';
import { Attendance } from './Attendance';
import { EVENTS_I_HOST } from './SecurityRules';

export interface BookingViewRow {
  ticket_booking_id: number;
  user_id: string;
  event_id: number;
  start_at: string;
  end_at: string;
  booked_quantity: number;
  ticket_quantity: number;
  ticket_title: string;
  price: string; // numeric(10,2)
  booking_status: string;
  institution_group_id: number; // recorded at event booking
  institution_id: number; // this is the event institution
  attendance_id: number;
  attendance_result: string;
  attendance_recorded_at: string;
  attendance_recorded_late: string; //computed front-end
  absence_reason: string;
  flow_option: string;
  event_feedback_id: number;
  content_score: number;
  delivery_score: number;
  admin_score: number;
  overall_score: number;
  computed_status: string; //computed front-end
}

const clone = makeClone();

export const BookingView: Entity<BookingViewRow> = {
  id: 'BookingView',
  title: 'Booking',
  service: 'main',
  rowLabel: (row: BookingViewRow) => [row.user_id, row.ticket_booking_id].join(' / '),
  rowLabelDynamic: (row: BookingViewRow) => `<lookup:UserView:${row.user_id}> / <lookup:EventView:${row.event_id}>`,
  primaryKeyFields: ['ticket_booking_id'],
  source: {
    tableName: 'public.booking_view',
  },
  security: [
    {
      role: 'user',
      action: 'read',
      access: 'conditional',
      condition: `user_id = '{userId}' OR event_id IN ${EVENTS_I_HOST}`,
    },
    {
      role: 'admin',
      action: 'all',
      access: 'conditional',
      condition: `user_id = '{userId}' OR event_id IN ${EVENTS_I_HOST} OR institution_id = {adminInstitutionId}`,
    },
    { role: 'staff', action: 'all', access: 'all' },
  ],
  defaultSort: [
    ['user_id', 'ASC'],
    ['start_at', 'DESC'],
  ],
  defaultFilters: [
    { fieldId: 'user_id', operator: 'eq', value: '' },
    { fieldId: 'event_id', operator: 'eq', value: '' },
    { fieldId: 'start_at', operator: 'nb', value: '' },
  ],
  fields: {
    ticket_booking_id: clone(Event.fields.id, { listColumn: false }),
    user_id: clone(TicketBooking.fields.user_id),
    event_id: clone(TicketBooking.fields.event_id),
    start_at: {
      seq: 16,
      type: FieldType.Datetime,
      label: 'Starts At',
      listColumn: true,
      getDisplayText: (value: string) => displayDateTimeToMinute(value, '-'),
      listColumnAlign: 'right',
      listColumnWidth: 145,
    },
    end_at: {
      seq: 18,
      type: FieldType.Datetime,
      label: 'Ends At',
      getDisplayText: (value: string) => displayDateTimeToMinute(value, '-'),
      listColumnAlign: 'right',
      listColumnWidth: 145,
    },
    booked_quantity: clone(TicketBooking.fields.quantity, { label: 'Booked Quantity', listColumn: true }),
    ticket_quantity: clone(Ticket.fields.quantity, { label: 'Ticket Quantity', listColumn: false }),
    ticket_title: clone(Ticket.fields.title, { label: 'Ticket Title', listColumn: true }),
    price: clone(Ticket.fields.price, { listColumn: true }),
    booking_status: clone(TicketBooking.fields.status, { label: 'Booking Status' }),
    institution_group_id: clone(TicketBooking.fields.institution_group_id, { listColumn: false }),
    institution_id: clone(Event.fields.institution_id, { listColumn: false }),
    attendance_id: {
      seq: 52,
      type: FieldType.Reference,
      refEntity: Attendance,
      label: 'Attendance Id',
      listColumn: false,
    },
    attendance_result: clone(Attendance.fields.attendance_result, { listColumn: false }),
    attendance_recorded_at: clone(Attendance.fields.created_at, { label: 'Attendance Recorded At', listColumn: false }),
    attendance_recorded_late: {
      seq: 57,
      type: FieldType.Text,
      label: 'Attendance Recorded Late?',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: BookingViewRow) =>
        !!row.attendance_recorded_at && row.attendance_recorded_at > row.end_at ? 'Y' : 'N',
    },
    absence_reason: clone(Attendance.fields.absence_reason, { listColumn: false }),
    flow_option: clone(Event.fields.flow_option, { listColumn: false }),
    event_feedback_id: clone(EventFeedback.fields.id, { label: 'Event Feedback Id', listColumn: false }),
    content_score: clone(EventFeedback.fields.content_score, { listColumn: false }),
    delivery_score: clone(EventFeedback.fields.delivery_score, { listColumn: false }),
    admin_score: clone(EventFeedback.fields.admin_score, { listColumn: false }),
    overall_score: clone(EventFeedback.fields.overall_score, { listColumn: false }),
    computed_status: {
      seq: 65,
      type: FieldType.Options,
      label: 'Status',
      listColumn: true,
      options: [
        { id: 'event cancelled', label: 'event cancelled' },
        { id: 'booking cancelled', label: 'booking cancelled' },
        { id: 'upcoming', label: 'upcoming' },
        { id: 'not tracked', label: 'not tracked' },
        { id: 'attendance not recorded', label: 'attendance not recorded' },
        { id: 'session did not take place', label: 'session did not take place' },
        { id: 'did not attend', label: 'did not attend' },
        { id: 'past, attendance not recorded', label: 'past, attendance not recorded' },
        { id: 'attended, pending feedback', label: 'attended, pending feedback' },
        { id: 'attended, complete', label: 'attended, complete' },
      ],
    },
  },
};

export interface ComputedStatusInput {
  booking_status: string;
  start_at: string;
  flow_option: string;
  attendance_id: number | null;
  attendance_result: string | null;
  event_feedback_id: number | null;
}

export type BookingComputedStatus =
  | 'event cancelled'
  | 'booking cancelled'
  | 'upcoming'
  | 'past, not tracked'
  | 'past, attendance not recorded'
  | 'session did not take place'
  | 'did not attend'
  | 'attended, pending feedback'
  | 'attended, complete'
  | 'added afterwards' // group booking matrix only
  | 'missing booking' // group booking matrix only
  | 'no booking' // group booking matrix only
  | 'hosting'; // user calendar only

export interface BookingComputedStatusOption extends Option {
  id: BookingComputedStatus;
  cellLabel: string;
  textColor: string;
  bgColor: string;
}

export const BookingComputedStatusOption: BookingComputedStatusOption[] = [
  { id: 'upcoming', label: 'upcoming', cellLabel: 'U', bgColor: '#2563EB', textColor: '#ffffff' },
  {
    id: 'past, not tracked',
    label: 'past, not tracked',
    cellLabel: 'NT',
    bgColor: '#B8E3C5',
    textColor: '#000000',
  },
  {
    id: 'attended, pending feedback',
    label: 'attended, pending feedback',
    cellLabel: 'AF',
    bgColor: '#48AD73',
    textColor: '#000000',
  },
  {
    id: 'attended, complete',
    label: 'attended, complete',
    cellLabel: 'AC',
    bgColor: '#206B47',
    textColor: '#ffffff',
  },
  {
    id: 'booking cancelled',
    label: 'booking cancelled',
    cellLabel: 'BX',
    bgColor: '#F4BDD0',
    textColor: '#000000',
  },
  {
    id: 'session did not take place',
    label: 'session did not take place',
    cellLabel: 'SN',
    bgColor: '#E77DA2',
    textColor: '#000000',
  },
  {
    id: 'event cancelled',
    label: 'event cancelled',
    cellLabel: 'EX',
    bgColor: '#E77DA2',
    textColor: '#000000',
  },
  {
    id: 'past, attendance not recorded',
    label: 'past, attendance not recorded',
    cellLabel: 'AR',
    bgColor: '#D44270',
    textColor: '#ffffff',
  },
  { id: 'did not attend', label: 'did not attend', cellLabel: 'DN', bgColor: '#AD2450', textColor: '#ffffff' },
];

export const GroupBookingComputedStatusOptions: BookingComputedStatusOption[] = [
  ...BookingComputedStatusOption,
  { id: 'missing booking', label: 'missing booking', cellLabel: 'MB', bgColor: '#701C49', textColor: '#ffffff' },
  {
    id: 'no booking',
    label: 'no booking',
    cellLabel: '-',
    bgColor: '#f1f1f1',
    textColor: '#000000',
  },
  {
    id: 'added afterwards',
    label: 'added afterwards',
    cellLabel: 'AA',
    bgColor: '#ffc107',
    textColor: '#212529',
  },
];

export const HostingStatusOption: BookingComputedStatusOption = {
  id: 'hosting',
  label: 'hosting',
  cellLabel: 'H',
  bgColor: '#ffc107',
  textColor: '#212529',
};

export const UserBookingComputedStatusOptions: BookingComputedStatusOption[] = [
  ...BookingComputedStatusOption,
  HostingStatusOption,
];

// non-group booking - needs to be replaced with the computed_status field from BookingView
export const getBookingComputedStatus = (input: ComputedStatusInput): BookingComputedStatus => {
  if (input.booking_status === 'canx-event') return 'event cancelled';
  if (input.booking_status === 'canx-booking') return 'booking cancelled';
  if (input.start_at > new Date().toISOString()) return 'upcoming';
  if (['none', 'noAttendance'].includes(input.flow_option)) return 'past, not tracked';
  if (typeof input.attendance_id !== 'number') return 'past, attendance not recorded';
  if (input.attendance_result === 'noSession') return 'session did not take place';
  if (input.attendance_result !== 'attended') return 'did not attend';
  if (typeof input.event_feedback_id !== 'number') return 'attended, pending feedback';
  return 'attended, complete';
};

BookingView.fields.computed_status.evaluateFrontEndIn = (row: BookingViewRow) => getBookingComputedStatus(row);
